import type { ErrorEvent } from '../types';

export class VideoRecorderError extends Error {
  public readonly type: ErrorEvent['type'];
  public readonly code?: string;
  public readonly retryable: boolean;
  public readonly trackingId?: string;

  constructor(
      message: string,
      type: ErrorEvent['type'],
      options: {
        code?: string;
        retryable?: boolean;
        trackingId?: string;
        cause?: Error;
      } = {}
  ) {
    // If your TS/lib supports ErrorOptions, you can do: super(message, { cause: options.cause })
    super(message);
    this.name = 'VideoRecorderError';
    this.type = type;
    this.code = options.code;
    this.retryable = options.retryable ?? false;
    this.trackingId = options.trackingId;

    // Assign cause safely even if lib.dom doesn't include it
    if (options.cause) {
      (this as unknown as { cause?: unknown }).cause = options.cause;
    }
  }

  toErrorEvent(): ErrorEvent {
    return {
      trackingId: this.trackingId,
      type: this.type,
      message: this.message,
      code: this.code,
      retryable: this.retryable
    };
  }
}

export class ErrorHandler {
  private static errorCounts = new Map<string, number>();
  private static maxRetries = 3;

  static handleRecordingError(error: Error, trackingId?: string): VideoRecorderError {
    if (error.name === 'NotAllowedError') {
      return new VideoRecorderError(
          'Camera access denied. Please allow camera permissions and try again.',
          'recording',
          { code: 'CAMERA_ACCESS_DENIED', retryable: true, trackingId }
      );
    }

    if (error.name === 'NotFoundError') {
      return new VideoRecorderError(
          'No camera device found. Please connect a camera and try again.',
          'recording',
          { code: 'CAMERA_NOT_FOUND', retryable: false, trackingId }
      );
    }

    if (error.name === 'NotReadableError') {
      return new VideoRecorderError(
          'Camera is already in use by another application.',
          'recording',
          { code: 'CAMERA_IN_USE', retryable: true, trackingId }
      );
    }

    if (error.name === 'OverconstrainedError') {
      return new VideoRecorderError(
          'Camera does not support the requested video quality. Try a lower quality setting.',
          'recording',
          { code: 'UNSUPPORTED_CONSTRAINTS', retryable: true, trackingId }
      );
    }

    return new VideoRecorderError(
        `Recording failed: ${error.message}`,
        'recording',
        { retryable: true, trackingId, cause: error }
    );
  }

  static handleUploadError(error: Error, trackingId?: string): VideoRecorderError {
    if (error.name === 'AbortError') {
      return new VideoRecorderError(
          'Upload was cancelled.',
          'upload',
          { code: 'UPLOAD_CANCELLED', retryable: false, trackingId }
      );
    }

    if (error.message.includes('NetworkError') || error.message.includes('fetch')) {
      return new VideoRecorderError(
          'Network error during upload. Please check your connection and try again.',
          'network',
          { code: 'NETWORK_ERROR', retryable: true, trackingId }
      );
    }

    if (error.message.includes('413') || /too large/i.test(error.message)) {
      return new VideoRecorderError(
          'File is too large for upload. Try recording a shorter video or reducing quality.',
          'upload',
          { code: 'FILE_TOO_LARGE', retryable: false, trackingId }
      );
    }

    if (error.message.includes('403') || /forbidden/i.test(error.message)) {
      return new VideoRecorderError(
          'Upload not authorized. Please check your authentication.',
          'upload',
          { code: 'UPLOAD_FORBIDDEN', retryable: false, trackingId }
      );
    }

    return new VideoRecorderError(
        `Upload failed: ${error.message}`,
        'upload',
        { retryable: true, trackingId, cause: error }
    );
  }

  static handleProcessingError(error: Error, trackingId?: string): VideoRecorderError {
    if (/timeout/i.test(error.message)) {
      return new VideoRecorderError(
          'Video processing timed out. The file may be too large or complex.',
          'processing',
          { code: 'PROCESSING_TIMEOUT', retryable: true, trackingId }
      );
    }

    if (/unsupported format/i.test(error.message)) {
      return new VideoRecorderError(
          'Video format not supported for processing.',
          'processing',
          { code: 'UNSUPPORTED_FORMAT', retryable: false, trackingId }
      );
    }

    return new VideoRecorderError(
        `Processing failed: ${error.message}`,
        'processing',
        { retryable: true, trackingId, cause: error }
    );
  }

  static shouldRetry(error: VideoRecorderError): boolean {
    if (!error.retryable) return false;

    const key = `${error.type}:${error.code ?? 'unknown'}`;
    const currentCount = this.errorCounts.get(key) ?? 0;

    if (currentCount >= this.maxRetries) {
      this.errorCounts.delete(key);
      return false;
    }

    this.errorCounts.set(key, currentCount + 1);
    return true;
  }

  static getRetryDelay(attemptNumber: number): number {
    // Exponential backoff with a cap at 30s
    const base = 1000 * Math.pow(2, attemptNumber);
    return Math.min(base, 30_000);
  }

  static clearRetryCount(errorType: string, code?: string): void {
    const key = `${errorType}:${code ?? 'unknown'}`;
    this.errorCounts.delete(key);
  }

  static logError(error: VideoRecorderError, context?: Record<string, unknown>): void {
    const hasWindow = typeof window !== 'undefined';
    const hasNavigator = typeof navigator !== 'undefined';
    const isDev =
        typeof process !== 'undefined' &&
        typeof process.env !== 'undefined' &&
        process.env.NODE_ENV === 'development';

    const logData = {
      timestamp: new Date().toISOString(),
      error: {
        message: error.message,
        type: error.type,
        code: error.code,
        retryable: error.retryable,
        trackingId: error.trackingId
      },
      context,
      userAgent: hasNavigator ? navigator.userAgent : 'unknown',
      url: hasWindow ? window.location.href : 'unknown'
    };

    if (isDev) {
      console.error('VideoRecorder Error:', logData);
    }

    if (typeof window !== 'undefined' && window.errorTracker) {
      window.errorTracker.captureException(error, logData);
    }
  }
}

export const createErrorHandler = (trackingId?: string) => {
  return {
    recording: (error: Error) => ErrorHandler.handleRecordingError(error, trackingId),
    upload: (error: Error) => ErrorHandler.handleUploadError(error, trackingId),
    processing: (error: Error) => ErrorHandler.handleProcessingError(error, trackingId)
  };
};
